import { HistoryOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Divider, Drawer, Space, Tooltip } from "antd";
import { ColumnProps } from "antd/lib/table";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppContext from "../../context/app/context";
import {
  ICustomerDissatisfactionReasonQuery,
  ICustomerSatisfactionAuditQuery,
} from "../../models/customer-satisfaction/query";
import {
  ICustomerDissatisfactionReason,
  ICustomerSatisfactionAudit,
} from "../../models/customer-satisfaction/response";
import {
  mapCustomerDissatisfactionReason,
  mapCustomerSatisfactionAudit,
  normalizeListingResponse,
} from "../../models/customer-satisfaction/mapper";
import EndPoints from "../../services/end-points";
import IBaseListingResponse from "../../utils/api/base-listing-response";
import { DEFAULT_QUERY } from "../../utils/helpers/constants";
import {
  errorNotification,
  successNotification,
} from "../../utils/helpers/notification";
import FieldBuilder from "../../components/form-components/field-builder";
import PageHeader from "../../components/general/layout/page-header";
import FormHookModal from "../../components/general/modals/form-hook-modal";
import RefreshBtn from "../../components/general/table-components/actions/refresh-btn";
import DeleteBtn from "../../components/general/table-components/actions/delete-btn";
import EditBtn from "../../components/general/table-components/actions/edit-btn";
import SearchFilterCard from "../../components/general/filter-card";
import Table from "../../components/general/table-components/table";
import AuditChangesList from "../../components/customer-satisfaction/audit-changes-list";
import OperationalSignalTags from "../../components/customer-satisfaction/operational-signal-tags";
import {
  CUSTOMER_DISSATISFACTION_OPERATIONAL_SIGNALS,
  CustomerDissatisfactionOperationalSignalKey,
  getCustomerDissatisfactionOperationalSignalLabel,
} from "../../models/customer-satisfaction/enum";
import styles from "./style.module.scss";

interface IReasonForm {
  reason: string;
  operationalSignals?: CustomerDissatisfactionOperationalSignalKey[];
}

const formatUserName = (user?: any) => {
  if (!user) return "-";
  return (
    user.fullName ||
    user.name ||
    `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ||
    `#${user.id}`
  );
};

const CustomerDissatisfactionReasonPage = () => {
  const { t } = useTranslation();
  const { direction } = useContext(AppContext);
  const [query, setQuery] =
    useState<ICustomerDissatisfactionReasonQuery>(DEFAULT_QUERY);
  const [search, setSearch] = useState<string | undefined>();
  const [list, setList] =
    useState<IBaseListingResponse<ICustomerDissatisfactionReason>>();
  const [audits, setAudits] =
    useState<IBaseListingResponse<ICustomerSatisfactionAudit>>();
  const [loading, setLoading] = useState<string[]>([]);
  const [formReason, setFormReason] =
    useState<ICustomerDissatisfactionReason>();
  const [auditReason, setAuditReason] =
    useState<ICustomerDissatisfactionReason>();

  const auditQuery: ICustomerSatisfactionAuditQuery = {
    page: 1,
    perPage: 50,
  };

  const toggleLoading = (key: string) => {
    setLoading((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key],
    );
  };

  const getData = async () => {
    try {
      toggleLoading("list");
      const { data } =
        await EndPoints.customerDissatisfactionReason.getAll(query);
      setList(normalizeListingResponse(data, mapCustomerDissatisfactionReason));
    } finally {
      toggleLoading("list");
    }
  };

  const getAudits = async (reasonId: number) => {
    try {
      toggleLoading("audits");
      const { data } = await EndPoints.customerDissatisfactionReason.getAudits(
        reasonId,
        auditQuery,
      );
      setAudits(normalizeListingResponse(data, mapCustomerSatisfactionAudit));
    } finally {
      toggleLoading("audits");
    }
  };

  useEffect(() => {
    getData();
  }, [query, direction]);

  const submitReason = async (data: IReasonForm) => {
    const reason = data.reason?.trim();
    if (!reason) {
      errorNotification(t("requiredField", { input: t("reason") }));
      return;
    }

    try {
      toggleLoading("submit");
      if (formReason?.id) {
        await EndPoints.customerDissatisfactionReason.update(formReason.id, {
          reason,
          operationalSignals: data.operationalSignals ?? [],
        });
        successNotification(t("updated_successfully"));
      } else {
        await EndPoints.customerDissatisfactionReason.create({
          reason,
          operationalSignals: data.operationalSignals ?? [],
        });
        successNotification(t("created_successfully"));
      }

      setFormReason(undefined);
      await getData();
    } finally {
      toggleLoading("submit");
    }
  };

  const columns: ColumnProps<ICustomerDissatisfactionReason>[] = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      align: "center",
      width: 80,
    },
    {
      title: t("reason"),
      dataIndex: "reason",
      key: "reason",
      align: "center",
    },
    {
      title: t("operational_signals"),
      dataIndex: "operationalSignals",
      key: "operationalSignals",
      align: "center",
      width: 260,
      render: (signals) => (
        <OperationalSignalTags signals={signals} size="small" />
      ),
    },
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      width: 170,
      render: (createdAt) =>
        createdAt ? moment(createdAt).format("YYYY-MM-DD HH:mm:ss") : "-",
    },
    {
      title: t("created_by"),
      dataIndex: "createdBy",
      key: "createdBy",
      align: "center",
      render: (createdBy) => formatUserName(createdBy),
    },
    {
      title: t("updated_at"),
      dataIndex: "updatedAt",
      key: "updatedAt",
      align: "center",
      width: 170,
      render: (updatedAt) =>
        updatedAt ? moment(updatedAt).format("YYYY-MM-DD HH:mm:ss") : "-",
    },
    {
      title: t("actions"),
      key: "actions",
      align: "center",
      width: 180,
      render: (_, record) => (
        <Space>
          <Tooltip title={t("audit_log")}>
            <Button
              type="primary"
              shape="circle"
              icon={<HistoryOutlined />}
              onClick={async () => {
                setAuditReason(record);
                setAudits(undefined);
                await getAudits(record.id);
              }}
            />
          </Tooltip>
          <EditBtn onClick={() => setFormReason(record)} />
          <DeleteBtn
            loading={loading.includes(`delete-${record.id}`)}
            onConfirm={async () => {
              try {
                toggleLoading(`delete-${record.id}`);
                await EndPoints.customerDissatisfactionReason.delete(record.id);
                successNotification(t("deleted_successfully"));
                await getData();
              } finally {
                toggleLoading(`delete-${record.id}`);
              }
            }}
          />
        </Space>
      ),
    },
  ];

  return (
    <div dir={direction} className={styles.container}>
      <PageHeader
        title={t("customer_dissatisfaction_reasons")}
        subTitle={t("customer_dissatisfaction_reasons_management")}
        extra={[
          <RefreshBtn
            key="refresh"
            loading={loading.includes("list")}
            onClick={() => getData()}
          >
            {t("refresh")}
          </RefreshBtn>,
          <Divider key="divider" type="vertical" />,
          <Button
            key="add"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setFormReason({} as ICustomerDissatisfactionReason)}
          >
            {t("add_customer_dissatisfaction_reason")}
          </Button>,
        ]}
      />
      <SearchFilterCard
        formId="customer-dissatisfaction-reason-filter"
        searchValue={search}
        setSearchValue={(value) => {
          setSearch(value);
          setQuery((current) => ({
            ...current,
            page: 1,
            keyword: value,
          }));
        }}
        onReset={() => {
          setSearch(undefined);
          setQuery({
            page: query.page,
            perPage: query.perPage,
          });
        }}
        resetLoading={loading.includes("list")}
        applyLoading={loading.includes("list")}
      />
      <Table<ICustomerDissatisfactionReason>
        rowKey="id"
        showPagination
        columns={columns}
        dataSource={list?.data ?? []}
        loading={loading.includes("list") || list === undefined}
        total={list?.total}
        pageSize={query.perPage}
        page={query.page}
        onPaginationChange={(page, perPage) => {
          setQuery((current) => ({
            ...current,
            page,
            perPage,
          }));
        }}
      />

      {formReason && (
        <FormHookModal<IReasonForm>
          id="customer-dissatisfaction-reason-form"
          title={
            formReason.id
              ? t("update_customer_dissatisfaction_reason")
              : t("add_customer_dissatisfaction_reason")
          }
          hideModal={() => setFormReason(undefined)}
          defaultValues={{
            reason: formReason.reason ?? "",
            operationalSignals: formReason.operationalSignals ?? [],
          }}
          onSubmit={submitReason}
          confirmButtonText={t("save")}
          loading={loading.includes("submit")}
          width={520}
        >
          <FieldBuilder
            name="reason"
            label={t("reason")}
            input={{ type: "text" }}
            rules={{ required: true }}
            width="large"
          />
          <FieldBuilder
            name="operationalSignals"
            label={t("operational_signals")}
            input={{
              type: "multiSelect",
              options: CUSTOMER_DISSATISFACTION_OPERATIONAL_SIGNALS.map(
                (signal) => ({
                  label: t(
                    getCustomerDissatisfactionOperationalSignalLabel(signal),
                  ),
                  value: signal,
                }),
              ),
            }}
            width="large"
          />
        </FormHookModal>
      )}

      <Drawer
        title={`${t("audit_log")}${auditReason ? `: ${auditReason.reason}` : ""}`}
        open={Boolean(auditReason)}
        onClose={() => {
          setAuditReason(undefined);
          setAudits(undefined);
        }}
        width={900}
        destroyOnClose
      >
        <AuditChangesList
          audits={audits?.data ?? []}
          loading={loading.includes("audits")}
        />
      </Drawer>
    </div>
  );
};

export default CustomerDissatisfactionReasonPage;
